import { DeliveryChannel } from '@pushly/models/lib/enums/delivery-channels.enum'

/**
 * App version is one property to the engine and two fields to the builder.
 *
 * The engine indexes app version under a single property, `profile.app_version`, for
 * every native platform, and a condition names the platform it scopes to in
 * `meta.channel` — there is no per-platform property. The field catalog instead offers a
 * channel-suffixed field per platform (`profile.app_version.native_ios`,
 * `profile.app_version.native_android`) so the two pick apart in the property picker, and
 * the builder resolves a condition's operator set and value input by that suffixed key.
 *
 * A value renderer receives the field and value but cannot rewrite the property of the
 * condition it edits, so the two shapes are reconciled here, at the shell's load/save
 * seam: a stored condition is opened against the suffixed field its channel names, and is
 * written back under the shared property with the channel restored to meta. Emitting the
 * suffixed key instead would store a property the engine has no index for — it would
 * derive a meaningless mask, take no channel scoping, and match nobody.
 *
 * `expandAppVersionChannels` and `collapseAppVersionChannels` are inverses on every shape
 * a segment persists: a stored document survives expand → (edit) → collapse unchanged.
 */

/** The shared property the engine stores every native app-version condition under. */
export const APP_VERSION_PROPERTY = 'profile.app_version'

/**
 * The native platforms an app-version condition can scope to.
 *
 * The catalog builds each field's key as `${APP_VERSION_PROPERTY}.${channel.toLowerCase()}`,
 * so the suffix names the channel and the channel names the suffix. Only these are
 * recognized: a condition carrying any other channel cannot be placed on a field, so it is
 * left on the bare property, where the builder surfaces it as unsupported rather than
 * scoping the segment to a platform the catalog does not offer.
 */
const APP_VERSION_CHANNELS: DeliveryChannel[] = [DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID]

const CHANNEL_BY_SUFFIX = new Map<string, DeliveryChannel>(
    APP_VERSION_CHANNELS.map((channel) => [channel.toLowerCase(), channel]),
)

const SUFFIXED_PREFIX = `${APP_VERSION_PROPERTY}.`

type Node = Record<string, unknown>

/** A leaf condition is a single-key object mapping one property to its operator/value. */
function readSoleProperty(node: Node): string | undefined {
    const keys = Object.keys(node)
    return keys.length === 1 ? keys[0] : undefined
}

/**
 * The group keys a condition-group document nests children under. `and_not` is a
 * first-class group alongside `and`/`or` everywhere else in the builder; the collapse and
 * expand seams must descend into all three, or a suffixed app-version key inside a group
 * this misses would escape the remap and reach the wire, where the engine cannot resolve it.
 */
const GROUP_KEYS = ['and', 'or', 'and_not'] as const

/**
 * Applies `rewriteLeaf` to every leaf condition in a condition-group document, recursing
 * through every group array segmentation nests. Groups and any other node are carried
 * through untouched; only leaf conditions are offered for rewriting.
 */
function transformConditions(node: Node, rewriteLeaf: (leaf: Node) => Node): Node {
    for (const key of GROUP_KEYS) {
        if (Array.isArray(node[key])) {
            return { ...node, [key]: (node[key] as Node[]).map((child) => transformConditions(child, rewriteLeaf)) }
        }
    }
    return rewriteLeaf(node)
}

/**
 * Load direction: the stored collapsed property becomes the suffixed field key the
 * builder resolves, chosen by `meta.channel`. A condition with no channel, or one naming a
 * platform the catalog does not offer, is left on the bare property untouched.
 */
export function expandAppVersionChannels(document: Record<string, unknown>): Record<string, unknown> {
    return transformConditions(document as Node, (leaf) => {
        if (readSoleProperty(leaf) !== APP_VERSION_PROPERTY) return leaf

        const inner = leaf[APP_VERSION_PROPERTY] as Node | undefined
        const channel = (inner?.meta as Node | undefined)?.channel
        if (typeof channel !== 'string') return leaf

        const suffix = channel.toLowerCase()
        if (!CHANNEL_BY_SUFFIX.has(suffix)) return leaf

        return { [`${SUFFIXED_PREFIX}${suffix}`]: inner }
    })
}

/**
 * Save direction: the suffixed field key becomes the shared property, with the field's
 * channel restored to `meta.channel`. The channel is set from the recognized suffix rather
 * than trusted from meta, so a fresh row — which carries no channel of its own — is scoped
 * correctly. A suffixed-looking property whose suffix names no known channel is left
 * untouched: collapsing it would drop the condition onto the shared property with no
 * channel, erasing the scoping the engine reads.
 */
export function collapseAppVersionChannels(document: Record<string, unknown>): Record<string, unknown> {
    return transformConditions(document as Node, (leaf) => {
        const property = readSoleProperty(leaf)
        if (property === undefined || !property.startsWith(SUFFIXED_PREFIX)) return leaf

        const channel = CHANNEL_BY_SUFFIX.get(property.slice(SUFFIXED_PREFIX.length))
        if (!channel) return leaf

        const inner = { ...(leaf[property] as Node) }
        inner.meta = { ...((inner.meta as Node | undefined) ?? {}), channel }

        return { [APP_VERSION_PROPERTY]: inner }
    })
}
